import { Box, Button, IconButton, Typography } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import React, { useRef, useEffect } from "react";
import { IoMdClose } from "react-icons/io";
import { MdAdd } from "react-icons/md";

export interface EventPeriod {
  inicio: Date | null;
  fim: Date | null;
}

interface Props {
  periods: EventPeriod[];
  onChange: (periods: EventPeriod[]) => void;
  disabled?: boolean;
}

const EventPeriodsInput: React.FC<Props> = ({ periods, onChange, disabled }) => {
  const newPeriodRef = useRef<HTMLDivElement | null>(null);
  const shouldFocusRef = useRef(false);

  const handleAddPeriod = () => {
    shouldFocusRef.current = true;
    onChange([...periods, { inicio: null, fim: null }]);
  };

  const handleRemovePeriod = (index: number) => {
    const newPeriods = periods.filter((_, i) => i !== index);
    onChange(newPeriods);
  };

  const handlePeriodChange = (
    index: number,
    field: keyof EventPeriod,
    value: Date | null
  ) => {
    const newPeriods = periods.map((period, i) =>
      i === index ? { ...period, [field]: value } : period
    );
    onChange(newPeriods);
  };

  useEffect(() => {
    if (shouldFocusRef.current && newPeriodRef.current) {
      const input = newPeriodRef.current.querySelector("input");
      if (input) {
        input.focus();
      }
      shouldFocusRef.current = false;
    }
  }, [periods.length]);

  const formatDisplayDate = (date: Date | null) => {
    if (!date) return "";
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Typography variant="body2" sx={{ fontSize: "13px", fontWeight: 500 }}>
        Período de hospedagem
      </Typography>

      {periods.map((period, index) => (
        <Box
          key={index}
          ref={index === periods.length - 1 ? newPeriodRef : null}
          display="flex"
          flexDirection="row"
          gap={1}
          alignItems="center"
          sx={{
            p: 1,
            border: "1px solid #e0e0e0",
            borderRadius: 1,
            backgroundColor: "#fafafa",
          }}
        >
          <Box flex={1}>
            <Typography variant="caption" sx={{ fontSize: "11px", color: "#666" }}>
              Data e hora início
            </Typography>
            <DateTimePicker
              value={period.inicio}
              onChange={(value) => handlePeriodChange(index, "inicio", value)}
              ampm={false}
              format="dd/MM/yyyy HH:mm"
              disabled={disabled}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  placeholder: "DD/MM/AAAA HH:MM",
                },
              }}
            />
          </Box>

          <Typography sx={{ mt: 2, color: "#666" }}>à</Typography>

          <Box flex={1}>
            <Typography variant="caption" sx={{ fontSize: "11px", color: "#666" }}>
              Data e hora fim
            </Typography>
            <DateTimePicker
              value={period.fim}
              onChange={(value) => handlePeriodChange(index, "fim", value)}
              ampm={false}
              format="dd/MM/yyyy HH:mm"
              disabled={disabled}
              minDateTime={period.inicio || undefined}
              defaultCalendarMonth={period.inicio || undefined}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  placeholder: "DD/MM/AAAA HH:MM",
                },
              }}
            />
          </Box>

          <IconButton
            onClick={() => handleRemovePeriod(index)}
            disabled={disabled}
            sx={{ mt: 2 }}
            size="small"
          >
            <IoMdClose color={disabled ? "#ccc" : "#d32f2f"} />
          </IconButton>
        </Box>
      ))}

      <Button
        variant="outlined"
        startIcon={<MdAdd />}
        onClick={handleAddPeriod}
        disabled={disabled}
        sx={{
          width: "100%",
          mx: "auto",
          px: "20%",
          py: 0.75,
          borderStyle: "dashed",
          borderColor: "#1976D2",
          color: "#1976D2",
          textTransform: "none",
          fontSize: "13px",
          fontWeight: 500,
          minHeight: "unset",
          "&:hover": {
            borderStyle: "dashed",
            borderColor: "#1565C0",
            backgroundColor: "rgba(25, 118, 210, 0.04)",
          },
        }}
      >
        Adicionar mais datas
      </Button>
    </Box>
  );
};

export default EventPeriodsInput;

export const formatPeriodForDisplay = (period: EventPeriod): string => {
  const formatDate = (date: Date | null) => {
    if (!date) return "--/--/---- --:--";
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  return `${formatDate(period.inicio)}  à  ${formatDate(period.fim)}`;
};

export const formatPeriodsForBackend = (periods: EventPeriod[]): { inicio: string; fim: string }[] => {
  return periods.map((period) => ({
    inicio: period.inicio ? period.inicio.toISOString().slice(0, 16) : "",
    fim: period.fim ? period.fim.toISOString().slice(0, 16) : "",
  }));
};

export const parsePeriodsFromBackend = (periods: { inicio: string; fim: string }[]): EventPeriod[] => {
  if (!periods || !Array.isArray(periods)) return [];
  return periods.map((period) => ({
    inicio: period.inicio ? new Date(period.inicio) : null,
    fim: period.fim ? new Date(period.fim) : null,
  }));
};
